import type { InputHTMLAttributes, ReactNode } from "react";

export function Field({
  label,
  htmlFor,
  hint,
  error,
  aside,
  optional = false,
  required = false,
  children,
}: {
  label: ReactNode;
  htmlFor?: string;
  hint?: ReactNode;
  error?: ReactNode;
  aside?: ReactNode;
  /** "Optional" after the label: the form works without it. */
  optional?: boolean;
  /** A red "*" after the label: the form cannot be saved without it. */
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={htmlFor} className="text-[13px] font-semibold">
          {label}
          <LabelMark required={required} optional={optional} />
        </label>
        {aside && <span className="text-xs text-muted">{aside}</span>}
      </div>
      {children}
      {error ? (
        <span role="alert" className="text-xs leading-snug text-err">
          {error}
        </span>
      ) : (
        hint && <span className="text-xs leading-snug text-muted">{hint}</span>
      )}
    </div>
  );
}

/** The app's one way of marking a field: a red "*" when required, a muted
 *  "Optional" when not. Use it in any label that is not a `Field`. */
export function LabelMark({
  required = false,
  optional = false,
}: {
  required?: boolean;
  optional?: boolean;
}) {
  // Drawn by CSS, so the label's text (and its accessible name) stays the plain label.
  if (required) return <span aria-hidden className="pl-0.5 text-err after:content-['*']" />;
  if (optional)
    return <span aria-hidden className="pl-1.5 text-xs font-normal text-faint after:content-['Optional']" />;
  return null;
}

/** A checkbox or radio with its label to the right, and an optional note below. */
export function Option({
  children,
  note,
  ...input
}: InputHTMLAttributes<HTMLInputElement> & { note?: ReactNode }) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 text-sm">
      <input className="mt-0.5 size-4 accent-ink" {...input} />
      <span className="flex flex-col gap-0.5">
        <span>{children}</span>
        {note && <span className="text-xs text-muted">{note}</span>}
      </span>
    </label>
  );
}
